// client/src/pages/business/WholesalerDetail.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Package, Search, X, Filter, ChevronDown,
  MapPin, ShoppingCart, Grid3X3, List, ArrowLeft,
  Check, AlertCircle, Lock, Unlock, Zap, Tag
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Navbar from '../../components/common/Navbar';
import StorefrontNavbar from '../../components/common/StorefrontNavbar';
import { setStorefrontContext } from '../../components/common/StorefrontBanner';
import Footer from '../../components/common/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FavoriteButton from '../../components/favorites/FavoriteButton';
import { wholesalersAPI, productDiscountsAPI, API_URL } from '../../services/api';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { getImageUrl } from '../../utils/imageHelper';
import useCategoryLabels from '../../hooks/useCategoryLabels';

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Wholesaler Header Component - Full Info Display
const WholesalerHeader = ({ wholesaler, id, isStorefront }) => {
  const { t } = useTranslation('business');
  
  // Helper to get service area label
  const getServiceAreaLabel = (area) => {
    const areaLabels = {
      heraklion: 'Ηράκλειο',
      chania: 'Χανιά',
      rethymno: 'Ρέθυμνο',
      lasithi: 'Λασίθι',
      athens_central: 'Κεντρική Αθήνα',
      athens_north: 'Βόρεια Αθήνα',
      athens_south: 'Νότια Αθήνα',
      piraeus: 'Πειραιάς',
    };
    return areaLabels[area] || area?.replace(/_/g, ' ');
  };
  
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8">
      <div className="flex flex-col md:flex-row gap-6">
        {/* Logo */}
        <div className="flex-shrink-0 self-start">
          <div className={`${isStorefront ? 'w-28 h-28 md:w-40 md:h-40' : 'w-24 h-24 md:w-32 md:h-32'} bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl flex items-center justify-center overflow-hidden`}>
            {wholesaler.logo ? (
              <img
                src={getImageUrl(wholesaler.logo)}
                alt={wholesaler.companyName}
                className="w-full h-full object-contain p-2"
                onError={(e) => {
                  if (!e.target.dataset.errorHandled) {
                    e.target.dataset.errorHandled = 'true';
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22%3E%3Crect fill=%22%23f3f4f6%22 width=%22100%22 height=%22100%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 dominant-baseline=%22middle%22 text-anchor=%22middle%22 font-family=%22sans-serif%22 font-size=%2224%22 fill=%22%239ca3af%22%3E?%3C/text%3E%3C/svg%3E';
                  }
                }}
              />
            ) : (
              <span className="text-4xl md:text-5xl font-bold text-blue-400">
                {wholesaler.companyName?.charAt(0) || 'W'}
              </span>
            )}
          </div>
        </div>

        {/* Main Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
                {wholesaler.companyName}
              </h1>
              {wholesaler.businessType && (
                <p className="text-gray-600 capitalize">
                  {wholesaler.businessType.replace(/_/g, ' ')}
                </p>
              )}
            </div>
            <div className="flex-shrink-0 hidden md:block">
              <FavoriteButton wholesalerId={id} size="lg" />
            </div>
          </div>

          {/* Description */}
          {wholesaler.description && (
            <p className="text-gray-600 text-base leading-relaxed mb-4 max-w-3xl">
              {wholesaler.description}
            </p>
          )}

          {/* Service Areas */}
          {wholesaler.serviceAreas && wholesaler.serviceAreas.length > 0 && (
            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-2">{t('wholesalers.deliversTo', 'Delivers to:')}</p>
              <div className="flex flex-wrap gap-1.5">
                {wholesaler.serviceAreas.slice(0, 4).map((area, index) => (
                  <span
                    key={index}
                    className="inline-block px-2.5 py-1 bg-emerald-100 text-emerald-800 text-sm rounded-lg font-medium"
                  >
                    {getServiceAreaLabel(area)}
                  </span>
                ))}
                {wholesaler.serviceAreas.length > 4 && (
                  <span className="inline-block px-2.5 py-1 bg-gray-100 text-gray-600 text-sm rounded-lg">
                    +{wholesaler.serviceAreas.length - 4} {t('wholesalers.more', 'more')}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Delivery Settings & Contact Row */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
            {/* Delivery Settings */}
            {wholesaler.deliverySettings?.minimumOrder > 0 && (
              <div className="flex items-center gap-1.5">
                <span>📦</span>
                <span>{t('wholesalers.minOrder', 'Min. order:')} €{wholesaler.deliverySettings.minimumOrder}</span>
              </div>
            )}
            {wholesaler.deliverySettings?.freeDeliveryThreshold > 0 && (
              <div className="flex items-center gap-1.5">
                <span>🚚</span>
                <span>{t('wholesalers.freeDeliveryOver', 'Free delivery over')} €{wholesaler.deliverySettings.freeDeliveryThreshold}</span>
              </div>
            )}
            
            {/* Contact Info */}
            {wholesaler.contact?.email && (
              <div className="flex items-center gap-1.5">
                <span>📧</span>
                <span>{wholesaler.contact.email}</span>
              </div>
            )}
            {wholesaler.contact?.phone && (
              <div className="flex items-center gap-1.5">
                <span>📞</span>
                <span>{wholesaler.contact.phone}</span>
              </div>
            )}
            
            {/* Location */}
            {wholesaler.location && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                <span>{wholesaler.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Favorite Button */}
        <div className="md:hidden self-start">
          <FavoriteButton wholesalerId={id} size="lg" />
        </div>
      </div>
    </div>
  );
};

// Search & Filter Bar Component
const FilterBar = ({
  searchInput,
  setSearchInput,
  handleSearchSubmit,
  selectedCategory,
  handleCategoryChange,
  availableCategories = [],
  selectedSubcategory,
  handleSubcategoryChange,
  availableSubcategories = [],
  sortBy,
  setSortBy,
  inStockOnly,
  setInStockOnly,
  showFilters,
  getCategoryLabel,
  setShowFilters,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
  clearFilters,
  activeFiltersCount,
  hidePricing,
  t
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
      {/* Search Row */}
      <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('catalog.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-12 pr-12 py-3 bg-gray-50 border-0 rounded-xl text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
          />
          {searchInput && (
            <button 
              type="button" 
              onClick={() => setSearchInput('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <div className="flex gap-3 shrink-0">
          <button
            type="submit"
            className="flex-1 sm:flex-none px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 font-medium"
          >
            <Search className="w-5 h-5" />
            <span className="sm:hidden">{t('common.search', 'Search')}</span>
            <span className="hidden sm:inline">{t('common.search', 'Search')}</span>
          </button>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`flex-1 sm:flex-none px-4 py-3 rounded-xl border transition-all flex items-center justify-center gap-2 font-medium ${
              showFilters || activeFiltersCount > 0
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-5 h-5" />
            <span className="sm:hidden">{t('common.filters', 'Filters')}</span>
            <span className="hidden sm:inline">{t('common.filters', 'Filters')}</span>
            {activeFiltersCount > 0 && (
              <span className="w-5 h-5 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center">
                {activeFiltersCount}
              </span>
            )}
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </form>

      {/* Expandable Filters */}
      <div className={`grid transition-all duration-300 overflow-hidden ${showFilters ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
        <div className="overflow-hidden">
          <div className="pt-4 border-t border-gray-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
              {/* Category */}
              <div className="sm:col-span-2 md:col-span-1">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  {t('catalog.category')}
                </label>
                <select 
                  value={selectedCategory} 
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  className="w-full max-w-full px-3 py-2 sm:px-4 sm:py-2.5 bg-gray-50 border-0 rounded-lg text-xs sm:text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 transition-all truncate"
                >
                  <option value="all">{t('catalog.allCategories')}</option>
                  {availableCategories.map(cat => (
                    <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
                  ))}
                </select>
              </div>

              {/* Subcategory — only show when subcategories exist */}
              {availableSubcategories.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  {t('catalog.subcategory', 'Subcategory')}
                </label>
                <select
                  value={selectedSubcategory}
                  onChange={(e) => handleSubcategoryChange(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 border-0 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  <option value="all">{t('catalog.allSubcategories', 'All Subcategories')}</option>
                  {availableSubcategories.map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              </div>
              )}

              {/* Min Price */}
              {/* Min Price — hidden when hidePricing */}
              {!hidePricing && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  {t('catalog.minPrice')}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  <input 
                    type="number" 
                    placeholder="0"
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 bg-gray-50 border-0 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>
              </div>
              )}

              {/* Max Price — hidden when hidePricing */}
              {!hidePricing && (
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  {t('catalog.maxPrice')}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                  <input 
                    type="number" 
                    placeholder={t('common.any', 'Any')}
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value)}
                    className="w-full pl-8 pr-4 py-2.5 bg-gray-50 border-0 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>
              </div>
              )}

              {/* Price range warning */}
              {!hidePricing && minPrice && maxPrice && Number(maxPrice) < Number(minPrice) && (
                <div className="col-span-full">
                  <p className="text-xs text-red-500">{t('catalog.priceRangeWarning', 'Max price is less than min price')}</p>
                </div>
              )}

              {/* Sort */}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                  {t('catalog.sortBy')}
                </label>
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 border-0 rounded-lg text-gray-900 focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  <option value="name_asc">{t('catalog.sortOptions.nameAsc')}</option>
                  <option value="name_desc">{t('catalog.sortOptions.nameDesc')}</option>
                  {!hidePricing && <option value="price_asc">{t('catalog.sortOptions.priceAsc')}</option>}
                  {!hidePricing && <option value="price_desc">{t('catalog.sortOptions.priceDesc')}</option>}
                </select>
              </div>

              {/* In Stock Toggle */}
              <div className="flex items-end">
                <label className="flex items-center gap-3 cursor-pointer group">
                  <div className={`relative w-12 h-6 rounded-full transition-colors ${inStockOnly ? 'bg-blue-600' : 'bg-gray-200'}`}>
                    <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${inStockOnly ? 'translate-x-6' : ''}`} />
                  </div>
                  <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                    {t('catalog.inStockOnly')}
                  </span>
                  <input 
                    type="checkbox" 
                    checked={inStockOnly}
                    onChange={(e) => setInStockOnly(e.target.checked)}
                    className="sr-only"
                  />
                </label>
              </div>
            </div>

            {/* Clear Filters */}
            {activeFiltersCount > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  {activeFiltersCount} {t('common.filtersActive', 'filters active')}
                </span>
                <button 
                  onClick={clearFilters}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                >
                  {t('common.clearAll', 'Clear All')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Product Card Component - Compact Version
const ProductCard = ({
  product,
  discount,
  flashOffer,
  quantity,
  onQuantityChange,
  onAddToCart,
  onNavigate,
  isAuthenticated,
  userRole,
  hidePricing,
  t,
  getCategoryLabel,
}) => {
  const stockStatus = product.stockStatus || 'in_stock';
  const originalPrice = product.pricing?.unitPrice || product.pricing?.basePrice || 0;
  
  // ⚡ Flash offer and customer discount can BOTH apply
  const hasFlashOffer = !!flashOffer;
  const hasDiscount = discount.active && discount.percentage > 0;
  
  // Flash offer = final price, no other discounts stacked on top
  let displayPrice = originalPrice;
  if (hasFlashOffer) {
    displayPrice = flashOffer.offerPrice;
  } else if (hasDiscount) {
    displayPrice = Math.round(originalPrice * (1 - discount.percentage / 100) * 100) / 100;
  }

  const savings = Math.round((originalPrice - displayPrice) * 100) / 100;
  const hasAnySaving = hasFlashOffer || hasDiscount;
  const isInStock = product.availability?.inStock !== false;
  const canAddToCart = isInStock && isAuthenticated && userRole === 'business';

  return (
    <div className="group bg-white rounded-lg border border-gray-100 overflow-hidden hover:shadow-md hover:border-gray-200 transition-all duration-200">
      {/* Image Container */}
      <div className="relative aspect-square bg-gray-50 overflow-hidden">
        {product.images && product.images.length > 0 ? (
          <img
            src={getImageUrl(product.images[0])}
            alt={product.name}
            className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-300"
            onClick={() => onNavigate(`/products/${product._id}`)}
            onError={(e) => {
              if (!e.target.dataset.errorHandled) {
                e.target.dataset.errorHandled = 'true';
                e.target.style.display = 'none';
                e.target.parentNode.classList.add('flex', 'items-center', 'justify-center');
                e.target.parentNode.innerHTML = '<div class="text-gray-300"><svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>';
              }
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-10 h-10 text-gray-300" />
          </div>
        )}

        {/* Badges — flash offer = final price, no other badges shown */}
        <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
          <div className="flex flex-col flex-wrap gap-1 max-w-[calc(100%-16px)]">
            {hasFlashOffer && (
              <span className="px-2 py-0.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold rounded-md shadow-sm flex items-center gap-1 animate-pulse">
                <Zap className="w-3 h-3" />
                {!hidePricing && flashOffer.originalPrice > 0
                  ? `-${Math.round(((flashOffer.originalPrice - flashOffer.offerPrice) / flashOffer.originalPrice) * 100)}%`
                  : 'OFFER'
                }
              </span>
            )}
            {!hasFlashOffer && !hidePricing && hasDiscount && discount.productPercentage > 0 && (
              <span className="px-2 py-0.5 bg-purple-600 text-white text-xs font-bold rounded-md shadow-sm inline-flex items-center gap-0.5">
                <Tag className="w-3 h-3" />
                -{discount.productPercentage}%
              </span>
            )}
            {!hasFlashOffer && !hidePricing && hasDiscount && (discount.catalogPercentage > 0 || discount.customerPercentage > 0) && (
              <span className="px-2 py-0.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs font-bold rounded-md shadow-sm">
                -{Math.round((discount.catalogPercentage + discount.customerPercentage) * 100) / 100}%
              </span>
            )}
          </div>
          <div className="ml-auto">
            <FavoriteButton productId={product._id} size="sm" />
          </div>
        </div>

        {/* Stock Status Badge */}
        {!isInStock && (
          <div className="absolute bottom-2 left-2">
            <span className="px-2 py-1 bg-red-500/90 backdrop-blur text-white text-xs font-medium rounded-md">
              {t('catalog.outOfStock')}
            </span>
          </div>
        )}
      </div>

      {/* Content - More compact padding */}
      <div className="p-3">
        {/* Category - Smaller */}
        <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">
          {getCategoryLabel(product.category)}
        </p>

        {/* Name - Smaller */}
        <h3 
          className="text-sm font-semibold text-gray-900 mb-1.5 cursor-pointer hover:text-blue-600 transition-colors line-clamp-2 leading-tight"
          onClick={() => onNavigate(`/products/${product._id}`)}
        >
          {product.name}
        </h3>

        {/* Price */}
        {hidePricing ? (
          <div className="mb-2">
            <span className="text-sm text-gray-400 italic">{t('catalog.priceOnAgreement', 'Price on agreement')}</span>
          </div>
        ) : (
        <div className="flex items-baseline flex-wrap gap-x-1.5 gap-y-0.5 mb-2">
          <span className={`text-lg font-bold ${hasFlashOffer ? 'text-red-600' : hasDiscount ? 'text-emerald-600' : 'text-gray-900'}`}>
            €{displayPrice.toFixed(2)}
          </span>
          {hasAnySaving && (
            <span className="text-xs text-red-400 line-through">
              €{originalPrice.toFixed(2)}
            </span>
          )}
          <span className="text-xs text-gray-500">
            /{t(`products:unitShort.${product.pricing?.unit}`, product.pricing?.unit)}
          </span>
        </div>
        )}

        {/* Savings Badge */}
        {!hidePricing && hasAnySaving && (
          <div className="mb-2">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-medium rounded">
              <Check className="w-2.5 h-2.5" />
              {t('catalog.save', 'Save')} €{(Math.round(savings * 100) / 100).toFixed(2)}
            </span>
          </div>
        )}

        {/* Stock Indicator - Smaller */}
        <div className="flex items-center gap-1.5 mb-2.5">
          <span className={`w-2 h-2 rounded-full ${
            stockStatus === 'in_stock' ? 'bg-emerald-500' :
            stockStatus === 'low_stock' ? 'bg-amber-500' : 'bg-red-500'
          }`} />
          <span className="text-[10px] text-gray-500">
            {stockStatus === 'in_stock' ? t('catalog.inStock') :
             stockStatus === 'low_stock' ? t('catalog.lowStock', 'Low Stock') : t('catalog.outOfStock')}
          </span>
          {product.pricing?.minOrderQuantity > 1 && (
            <span className="text-[10px] text-gray-400 ml-auto">
              Min: {product.pricing.minOrderQuantity}
            </span>
          )}
        </div>

        {/* Add to Cart - Compact */}
        {canAddToCart && (
          <div className="flex gap-1.5">
            <input
              type="number"
              min={product.pricing?.minOrderQuantity || 1}
              step={product.pricing?.minOrderQuantity || 1}
              value={quantity}
              onChange={(e) => onQuantityChange(product._id, e.target.value)}
              className="w-12 px-1 py-1.5 text-center text-sm bg-gray-50 border-0 rounded focus:ring-2 focus:ring-blue-500"
            />
            <button 
              onClick={() => onAddToCart(product)}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium text-xs"
            >
              <ShoppingCart className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{t('catalog.addToCart')}</span>
              <span className="sm:hidden">{t('catalog.add', 'Add')}</span>
            </button>
          </div>
        )}

        {/* View Details for non-authenticated */}
        {!canAddToCart && (
          <Link 
            to={`/products/${product._id}`}
            className="block text-center py-1.5 text-blue-600 hover:text-blue-700 font-medium text-xs transition-colors"
          >
            {t('catalog.viewDetails')} →
          </Link>
        )}
      </div>
    </div>
  );
};

// Product List Item Component - Horizontal Row Version
const ProductListItem = ({
  product, discount, flashOffer, quantity,
  onQuantityChange, onAddToCart, onNavigate,
  isAuthenticated, userRole, hidePricing, t,
  getCategoryLabel,
}) => {
  const originalPrice = product.pricing?.unitPrice || product.pricing?.basePrice || 0;
  const hasFlashOffer = !!flashOffer;
  const hasDiscount = discount.active && discount.percentage > 0;
  
  // Flash offer = final price, no other discounts stacked on top
  let displayPrice = originalPrice;
  if (hasFlashOffer) {
    displayPrice = flashOffer.offerPrice;
  } else if (hasDiscount) {
    displayPrice = Math.round(originalPrice * (1 - discount.percentage / 100) * 100) / 100;
  }
  
  const isInStock = product.availability?.inStock !== false;
  const canAddToCart = isInStock && isAuthenticated && userRole === 'business';
  const stockStatus = product.stockStatus || 'in_stock';

  return (
    <div className="bg-white border border-gray-100 rounded-lg hover:shadow-sm hover:border-gray-200 transition-all px-3 py-2.5 sm:px-4 sm:py-2.5">
      {/* Desktop: single row | Mobile: two rows */}
      <div className="hidden sm:flex items-center gap-3">
        {/* Desktop Thumbnail */}
        <div
          className="w-12 h-12 rounded-md bg-gray-50 overflow-hidden flex-shrink-0 cursor-pointer"
          onClick={() => onNavigate(`/products/${product._id}`)}
        >
          {product.images && product.images.length > 0 ? (
            <img src={getImageUrl(product.images[0])} alt={product.name} className="w-full h-full object-cover"
              onError={(e) => { if (!e.target.dataset.errorHandled) { e.target.dataset.errorHandled='true'; e.target.style.display='none'; }}} />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Package className="w-5 h-5 text-gray-300" /></div>
          )}
        </div>
        {/* Desktop Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 leading-tight cursor-pointer hover:text-blue-600 transition-colors line-clamp-1" onClick={() => onNavigate(`/products/${product._id}`)}>
            {product.name}
          </h3>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-gray-400">{getCategoryLabel(product.category)}</span>
            {product.brand && <span className="text-[10px] text-gray-400">• {product.brand}</span>}
            <span className={`w-2 h-2 rounded-full ${stockStatus === 'in_stock' ? 'bg-emerald-500' : stockStatus === 'low_stock' ? 'bg-amber-500' : 'bg-red-500'}`} />
            <span className="text-[10px] text-gray-500">{stockStatus === 'in_stock' ? t('catalog.inStock') : stockStatus === 'low_stock' ? t('catalog.lowStock', 'Low Stock') : t('catalog.outOfStock')}</span>
            {product.sku && <span className="text-[10px] text-gray-400">{product.sku}</span>}
          </div>
        </div>
        {/* Desktop Badges — flash = final price, hide other badges */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {hasFlashOffer && (
            <span className="px-1.5 py-0.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-bold rounded flex items-center gap-0.5">
              <Zap className="w-2.5 h-2.5" /> OFFER
            </span>
          )}
          {!hasFlashOffer && !hidePricing && hasDiscount && (
            <span className="px-1.5 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded">
              -{Math.round(discount.percentage * 100) / 100}%
            </span>
          )}
          <FavoriteButton productId={product._id} size="sm" />
        </div>
        {/* Desktop Price */}
        <div className="flex-shrink-0 text-right w-24">
          {hidePricing ? (
            <span className="text-xs text-gray-400 italic">{t('catalog.priceOnAgreement', 'Price on agreement')}</span>
          ) : (
            <>
              <span className={`text-base font-bold ${hasFlashOffer ? 'text-red-600' : hasDiscount ? 'text-emerald-600' : 'text-gray-900'}`}>
                €{displayPrice.toFixed(2)}
              </span>
              <span className="text-[10px] text-gray-500 block">/{t(`products:unitShort.${product.pricing?.unit}`, product.pricing?.unit)}</span>
              {(hasFlashOffer || hasDiscount) && (
                <span className="text-[10px] text-red-400 line-through">€{originalPrice.toFixed(2)}</span>
              )}
            </>
          )}
        </div>
        {/* Desktop Cart */}
        {canAddToCart && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <input type="number" min={product.pricing?.minOrderQuantity || 1} step={product.pricing?.minOrderQuantity || 1} value={quantity} onChange={(e) => onQuantityChange(product._id, e.target.value)}
              className="w-12 px-1 py-1.5 text-center text-sm bg-gray-50 border-0 rounded focus:ring-2 focus:ring-blue-500" />
            <button onClick={() => onAddToCart(product)}
              className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors">
              {t('catalog.add', 'Add')}
            </button>
          </div>
        )}
      </div>

      {/* Mobile: two-row layout */}
      <div className="sm:hidden">
        {/* Row 1: Name + Price */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-xs font-semibold text-gray-900 leading-snug cursor-pointer hover:text-blue-600 transition-colors line-clamp-2" onClick={() => onNavigate(`/products/${product._id}`)}>
              {product.name}
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${stockStatus === 'in_stock' ? 'bg-emerald-500' : stockStatus === 'low_stock' ? 'bg-amber-500' : 'bg-red-500'}`} />
              <span className="text-[10px] text-gray-400 truncate">{getCategoryLabel(product.category)}</span>
              {product.sku && <span className="text-[10px] text-gray-400 flex-shrink-0">#{product.sku}</span>}
            </div>
          </div>
          <div className="flex-shrink-0 text-right">
            {hidePricing ? (
              <span className="text-[10px] text-gray-400 italic">{t('catalog.priceOnAgreement', 'Price on agreement')}</span>
            ) : (
              <>
                <span className={`text-xs font-bold ${hasFlashOffer ? 'text-red-600' : hasDiscount ? 'text-emerald-600' : 'text-gray-900'}`}>
                  €{displayPrice.toFixed(2)}
                </span>
                <span className="text-[10px] text-gray-500 block">/{t(`products:unitShort.${product.pricing?.unit}`, product.pricing?.unit)}</span>
              </>
            )}
          </div>
        </div>
        {/* Row 2: Badges + Cart */}
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-1">
            {hasFlashOffer && (
              <span className="px-1.5 py-0.5 bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-bold rounded flex items-center gap-0.5">
                <Zap className="w-2.5 h-2.5" /> OFFER
              </span>
            )}
            {!hasFlashOffer && !hidePricing && hasDiscount && (
              <span className="px-1.5 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded">
                -{Math.round(discount.percentage * 100) / 100}%
              </span>
            )}
            {(hasFlashOffer || hasDiscount) && !hidePricing && (
              <span className="text-[10px] text-red-400 line-through">€{originalPrice.toFixed(2)}</span>
            )}
            <FavoriteButton productId={product._id} size="sm" />
          </div>
          {canAddToCart && (
            <div className="flex items-center gap-2">
              <input type="number" min={product.pricing?.minOrderQuantity || 1} step={product.pricing?.minOrderQuantity || 1} value={quantity} onChange={(e) => onQuantityChange(product._id, e.target.value)}
                className="w-14 h-9 px-1 text-center text-sm bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500" />
              <button onClick={() => onAddToCart(product)}
                className="h-9 px-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                <ShoppingCart className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ clearFilters, t }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
    <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
      <Package className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">
      {t('catalog.noProducts', 'No products found')}
    </h3>
    <p className="text-gray-500 mb-6 max-w-md mx-auto">
      {t('catalog.noProductsDescription', 'Try adjusting your filters or search terms to find what you\'re looking for.')}
    </p>
    <button 
      onClick={clearFilters}
      className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
    >
      {t('common.clearFilters', 'Clear Filters')}
    </button>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const WholesalerDetail = () => {
  const { t } = useTranslation(['business', 'common', 'products']);
  const { getCategoryLabel } = useCategoryLabels();
  const { id: rawIdOrSlug } = useParams();
  const navigate = useNavigate();
  // ✅ FIX: Destructure 'items' correctly from useCart
  const { addToCart, items: cartItems } = useCart();
  const { isAuthenticated, user } = useAuth();

  // Slug resolution: if param is not a MongoDB ObjectId, resolve it
  const [resolvedId, setResolvedId] = useState(null);
  const [slugResolving, setSlugResolving] = useState(false);
  const isObjectId = /^[0-9a-fA-F]{24}$/.test(rawIdOrSlug);
  const id = isObjectId ? rawIdOrSlug : resolvedId;
  const isStorefront = !isObjectId; // Custom slug URL → storefront mode
  
  // Data States
  const [wholesaler, setWholesaler] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [discounts, setDiscounts] = useState([]); // Array of discounts
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessDeniedData, setAccessDeniedData] = useState(null);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [flashOffers, setFlashOffers] = useState([]);
  const [catalogDiscounts, setCatalogDiscounts] = useState(null);
  const [productDiscounts, setProductDiscounts] = useState({});
  const [discountDropdownOpen, setDiscountDropdownOpen] = useState(false);

  // ── Enterprise Storefront State ──
  const [hidePricing, setHidePricing] = useState(false);
  const [activeTab, setActiveTab] = useState('full'); // 'agreed' | 'full'
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('wholesalerDetail_viewMode') || 'grid'); // 'grid' | 'list'
  const [agreedProducts, setAgreedProducts] = useState([]);
  const [agreedCategories, setAgreedCategories] = useState([]);
  const [hasAgreedCatalog, setHasAgreedCatalog] = useState(false);
  const [agreedLoading, setAgreedLoading] = useState(false);
  const [agreedTotal, setAgreedTotal] = useState(0);

  useEffect(() => { localStorage.setItem('wholesalerDetail_viewMode', viewMode); }, [viewMode]);

  // Slug resolution effect
  useEffect(() => {
    if (!isObjectId && rawIdOrSlug) {
      setSlugResolving(true);
      wholesalersAPI.resolveSlug(rawIdOrSlug)
        .then(res => {
          if (res.data.success && res.data.wholesaler?._id) {
            setResolvedId(res.data.wholesaler._id);
          } else {
            setError(t('common.wholesalerNotFound', 'Wholesaler not found'));
          }
        })
        .catch(() => {
          setError(t('common.wholesalerNotFound', 'Wholesaler not found'));
        })
        .finally(() => setSlugResolving(false));
    }
  }, [rawIdOrSlug, isObjectId, t]);

  
  // ✅ NEW: Calculate Cart Total for THIS wholesaler only
  const currentCartTotal = useMemo(() => {
    if (!cartItems || cartItems.length === 0) return 0;
    return cartItems
      .filter(item => {
        // Handle varying ID structures safely
        const wId = item.wholesalerId?._id || item.wholesalerId || item.wholesaler?._id || item.wholesaler;
        return wId === id;
      })
      .reduce((sum, item) => sum + ((item.pricing?.unitPrice || item.price || 0) * item.quantity), 0);
  }, [cartItems, id]);

  // ✅ NEW: Dynamic Active Discount based on Cart Total
  // Only sums up discounts where cartTotal >= minAmount
  // Customer discount (existing logic)
  const customerDiscount = useMemo(() => {
    if (discounts.length === 0) return { active: false, percentage: 0, targeted: [] };
    const unlocked = discounts.filter(d => currentCartTotal >= (d.minimumOrderAmount || 0));
    if (unlocked.length === 0) return { active: false, percentage: 0, targeted: [] };
    const flat = unlocked.filter(d => !d.targetType || d.targetType === 'all');
    const targeted = unlocked.filter(d => d.targetType && d.targetType !== 'all');
    const flatPct = Math.min(flat.reduce((sum, d) => sum + d.percentage, 0), 100);
    return { active: flat.length > 0 || targeted.length > 0, percentage: flatPct, targeted };
  }, [discounts, currentCartTotal]);

  // Per-product catalog discount calculator — handles targeted discounts correctly
  // MUST be defined before getProductDiscount which depends on it
  const getProductCatalogPct = useCallback((product) => {
    if (!catalogDiscounts?.discounts?.length) return 0;
    const discountData = catalogDiscounts.discounts;

    // For each discount, determine if it applies to this product and get its percentage
    const applicableDiscounts = discountData.map(d => {
      if (d.type === 'flat') {
        return { pct: d.percentage || 0, stackable: d.stackable };
      }
      if (d.type === 'targeted') {
        // Only apply if product matches targetType/targetValue
        if (d.targetType === 'brand') {
          if ((product.brand || '').toLowerCase() === (d.targetValue || '').toLowerCase()) {
            return { pct: d.targetPercentage || 0, stackable: d.stackable };
          }
          return null; // doesn't match — skip
        }
        if (d.targetType === 'category') {
          if ((product.category || '').toLowerCase() === (d.targetValue || '').toLowerCase()) {
            return { pct: d.targetPercentage || 0, stackable: d.stackable };
          }
          return null;
        }
        if (d.targetType === 'subcategory') {
          if ((product.subcategory || '').toLowerCase() === (d.targetValue || '').toLowerCase()) {
            return { pct: d.targetPercentage || 0, stackable: d.stackable };
          }
          return null;
        }
        return null;
      }
      if (d.type === 'tiered') {
        // Tiered applies at order level, not product level — include with 0 for display
        // (actual tiered % is calculated at cart/order time based on order total)
        return { pct: 0, stackable: d.stackable };
      }
      return null;
    }).filter(Boolean);

    if (applicableDiscounts.length === 0) return 0;

    const stackable = applicableDiscounts.filter(a => a.stackable);
    const nonStackable = applicableDiscounts.filter(a => !a.stackable);

    const stackedPct = stackable.reduce((sum, a) => sum + (a.pct || 0), 0);
    const bestNonStackable = nonStackable.reduce((best, a) => (a.pct || 0) > best ? (a.pct || 0) : best, 0);

    return Math.round(Math.min(Math.max(stackedPct, bestNonStackable), 100) * 100) / 100;
  }, [catalogDiscounts]);

  // Per-product combined discount: catalog + (product-specific OR customer, whichever is higher)
  // Rule: product-specific discount REPLACES customer discount for that item (does not stack)
  // Targeted customer discounts are ADDITIVE on top of flat
  const getProductDiscount = useCallback((product) => {
    const catalogPct = getProductCatalogPct(product);
    let customerPct = customerDiscount.active ? customerDiscount.percentage : 0;

    // Add targeted customer discounts that match this product
    if (customerDiscount.targeted && customerDiscount.targeted.length > 0) {
      for (const td of customerDiscount.targeted) {
        if (td.targetType === 'category' && (product.category || '').toLowerCase() === (td.targetValue || '').toLowerCase()) {
          customerPct += td.percentage;
        } else if (td.targetType === 'subcategory' && (product.subcategory || '').toLowerCase() === (td.targetValue || '').toLowerCase()) {
          customerPct += td.percentage;
        }
      }
      if (customerPct > 100) customerPct = 100;
    }

    const productPct = productDiscounts[product._id]?.percentage || 0;

    // Product discount replaces customer discount — pick the higher of the two
    const effectivePct = productPct > 0 ? Math.max(productPct, customerPct) : customerPct;

    if (catalogPct === 0 && effectivePct === 0) return { active: false, percentage: 0, catalogPercentage: 0, customerPercentage: customerPct, productPercentage: productPct };
    const combined = Math.round((catalogPct + effectivePct) * 100) / 100;
    return { active: true, percentage: Math.min(combined, 100), catalogPercentage: catalogPct, customerPercentage: productPct > 0 ? 0 : customerPct, productPercentage: productPct };
  }, [getProductCatalogPct, customerDiscount, productDiscounts]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const productsPerPage = 50;

  // Filters — restore from sessionStorage if returning to same wholesaler
  const savedFilters = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(`wd_filters_${id}`);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }, [id]);
  const [searchTerm, setSearchTerm] = useState(savedFilters?.searchTerm || '');
  const [searchInput, setSearchInput] = useState(savedFilters?.searchTerm || '');
  const [selectedCategory, setSelectedCategory] = useState(savedFilters?.selectedCategory || 'all');
  const [minPrice, setMinPrice] = useState(savedFilters?.minPrice || '');
  const [maxPrice, setMaxPrice] = useState(savedFilters?.maxPrice || '');
  const [inStockOnly, setInStockOnly] = useState(savedFilters?.inStockOnly || false);
  const [sortBy, setSortBy] = useState(savedFilters?.sortBy || 'name_asc');
  const [showFilters, setShowFilters] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(savedFilters?.selectedSubcategory || 'all');
  const [availableSubcategories, setAvailableSubcategories] = useState([]);

  // Persist filters to sessionStorage
  useEffect(() => {
    if (!id) return;
    const hasFilters = searchTerm || selectedCategory !== 'all' || selectedSubcategory !== 'all' || minPrice || maxPrice || inStockOnly || sortBy !== 'name_asc';
    if (hasFilters) {
      sessionStorage.setItem(`wd_filters_${id}`, JSON.stringify({ searchTerm, selectedCategory, selectedSubcategory, minPrice, maxPrice, inStockOnly, sortBy }));
    } else {
      sessionStorage.removeItem(`wd_filters_${id}`);
    }
  }, [id, searchTerm, selectedCategory, selectedSubcategory, minPrice, maxPrice, inStockOnly, sortBy]);

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedCategory !== 'all') count++;
    if (selectedSubcategory !== 'all') count++;
    if (minPrice) count++;
    if (maxPrice) count++;
    if (inStockOnly) count++;
    return count;
  }, [searchTerm, selectedCategory, selectedSubcategory, minPrice, maxPrice, inStockOnly]);

  // Fetch wholesaler data
  const fetchWholesaler = useCallback(async () => {
    try {
      const response = await wholesalersAPI.getById(id);
      setWholesaler(response.data.wholesaler || response.data);
    } catch (err) {
      setError(t('common.errorLoadingData', 'Failed to load data'));
      console.error(err);
    }
  }, [id, t]);

  // Fetch discount(s)
  const fetchDiscount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const profileRes = await axios.get(`${API_URL}/business/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const businessId = profileRes.data.business?._id;

      if (businessId) {
        // Try new endpoint first (returns individual discounts)
        try {
          const discountRes = await axios.get(
            `${API_URL}/discounts/customers/business/${businessId}/details?wholesalerId=${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (discountRes.data.success && discountRes.data.hasDiscounts) {
            setDiscounts(discountRes.data.discounts.map(d => ({
              active: true,
              percentage: d.percentage,
              reason: d.reason,
              minimumOrderAmount: d.minimumOrderAmount || 0,
              targetType: d.targetType || 'all',
              targetValue: d.targetValue || null
            })));
            return;
          }
        } catch (detailsError) {
          console.log('Falling back to combined discount endpoint');
        }

        // Fallback
        const discountRes = await axios.get(
          `${API_URL}/discounts/customers/business/${businessId}?wholesalerId=${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (discountRes.data.success && discountRes.data.hasDiscount) {
          const discountData = discountRes.data.discount;
          setDiscounts([{
            active: true,
            percentage: discountData.percentage,
            reason: discountData.reason,
            minimumOrderAmount: discountData.minimumOrderAmount || 0
          }]);
        }
      }
    } catch (err) {
      console.error('Error fetching discount:', err);
    }
  }, [id]);

  // Fetch active flash offers for this wholesaler
  const fetchFlashOffers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !id) return;
      const res = await axios.get(`${API_URL}/flash-offers/wholesaler/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFlashOffers(res.data.offers || []);
    } catch (err) {
      // Silent fail — flash offers are supplementary
      console.log('Flash offers not available');
    }
  }, [id]);

  // Fetch catalog-wide discounts for this wholesaler (public, no auth needed)
  const fetchCatalogDiscounts = useCallback(async () => {
    try {
      if (!id) return;
      const res = await axios.get(`${API_URL}/catalog-discounts/wholesaler/${id}`);
      if (res.data.discounts && res.data.discounts.length > 0) {
        setCatalogDiscounts({ discounts: res.data.discounts, count: res.data.discounts.length });
      }
    } catch (err) {
      console.log('Catalog discounts not available');
    }
  }, [id]);

  // Helper: get flash offer for a specific product
  const getFlashOfferForProduct = useCallback((productId) => {
    let bestDeal = null;
    for (const offer of flashOffers) {
      const item = offer.items?.find(i => i.productId === productId);
      if (item) {
        // If multiple offers cover the same product, pick the lowest price
        if (!bestDeal || item.offerPrice < bestDeal.offerPrice) {
          bestDeal = { ...item, offerId: offer._id, offerTitle: offer.title, endDate: offer.endDate };
        }
      }
    }
    return bestDeal;
  }, [flashOffers]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      if (currentPage === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = {
        page: currentPage,
        limit: productsPerPage,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        subcategory: selectedSubcategory !== 'all' ? selectedSubcategory : undefined,
        search: searchTerm || undefined,
        sort: sortBy !== 'name_asc' ? sortBy : undefined,
        minPrice: minPrice || undefined,
        maxPrice: maxPrice || undefined
      };

      const response = await wholesalersAPI.getCatalog(id, params);
      
      // ✅ PHASE 6: Check for access denied
      if (response.data.accessDenied) {
        console.log('🔒 Access denied to private catalog');
        setAccessDenied(true);
        setAccessDeniedData({
          privateCatalogMode: response.data.privateCatalogMode,
          hasPendingRequest: response.data.hasPendingRequest,
          wholesaler: response.data.wholesaler,
          message: response.data.message
        });
        setLoading(false);
        setLoadingMore(false);
        return; // Stop here - don't try to load products
      }
      
      const productsData = response.data.products || [];
      const pagination = {
        total: response.data.total || 0,
        totalPages: response.data.totalPages || 1,
        currentPage: response.data.currentPage || 1
      };

      if (currentPage === 1) {
        setProducts(productsData);
      } else {
        setProducts(prev => [...prev, ...productsData]);
      }

      setTotalProducts(pagination.total);
      setTotalPages(pagination.totalPages);

      // Store dynamic categories and subcategories from this wholesaler's products
      if (response.data.categories) {
        setAvailableCategories(response.data.categories);
      }
      if (response.data.subcategories) {
        setAvailableSubcategories(response.data.subcategories);
      } else {
        setAvailableSubcategories([]);
      }

      // Enterprise: read hidePricing flag from response
      if (response.data.hidePricing !== undefined) {
        setHidePricing(response.data.hidePricing);
      }

      // Initialize quantities
      const newQuantities = {};
      productsData.forEach(product => {
        if (!quantities[product._id]) {
          newQuantities[product._id] = product.pricing?.minOrderQuantity || 1;
        }
      });
      setQuantities(prev => ({ ...prev, ...newQuantities }));

    } catch (err) {
      setError(t('common.errorLoadingProducts', 'Failed to load products'));
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [id, currentPage, selectedCategory, selectedSubcategory, searchTerm, sortBy, minPrice, maxPrice, productsPerPage, quantities, t]);

  // ✅ PHASE 6: Handle Request Access
  const handleRequestAccess = async () => {
    try {
      setRequestingAccess(true);
      await wholesalersAPI.requestCatalogAccess(id);
      
      // Update UI to show pending state
      setAccessDeniedData(prev => ({
        ...prev,
        hasPendingRequest: true
      }));

      // Show success message (you can use a toast library or alert)
      toast.success(t('catalog.requestSent', 'Access request sent! You will be notified when approved.'));
      
    } catch (err) {
      console.error('Request access error:', err);
      toast.error(t('catalog.requestFailed', 'Failed to send access request. Please try again.'));
    } finally {
      setRequestingAccess(false);
    }
  };

  // Fetch product-specific discounts for this business
  const fetchProductDiscounts = useCallback(async () => {
    if (!id || !isAuthenticated || user?.role !== 'business') return;
    try {
      const res = await productDiscountsAPI.getMyDiscounts(id);
      if (res.data?.success && res.data.discounts && typeof res.data.discounts === 'object') {
        setProductDiscounts(res.data.discounts);
      }
    } catch (e) {
      // Silent — product discounts are supplementary
    }
  }, [id, isAuthenticated, user?.role]);

  // Effects
  useEffect(() => {
    if (!id) return; // Wait for slug resolution
    fetchWholesaler();
    fetchCatalogDiscounts();
    if (isAuthenticated && user?.role === 'business') {
      fetchDiscount();
      fetchFlashOffers();
      fetchProductDiscounts();
      // Fetch agreed catalog (Enterprise custom catalog for this business)
      fetchAgreedCatalog();
    }
  }, [id, isAuthenticated, user, fetchWholesaler, fetchDiscount, fetchFlashOffers, fetchCatalogDiscounts, fetchProductDiscounts]);

  // Storefront mode: persist context to sessionStorage for cross-page navigation
  useEffect(() => {
    if (isStorefront && wholesaler && id) {
      setStorefrontContext({
        slug: rawIdOrSlug,
        wholesalerName: wholesaler.companyName,
        wholesalerId: id,
        logo: wholesaler.logo || null
      });
    }
  }, [isStorefront, wholesaler, id, rawIdOrSlug]);

  // Fetch agreed/custom catalog for this business
  const fetchAgreedCatalog = useCallback(async () => {
    if (!id || !isAuthenticated || user?.role !== 'business') return;
    try {
      setAgreedLoading(true);
      const res = await wholesalersAPI.getMyCatalog(id, { limit: 500 });
      if (res.data.success && res.data.hasCatalog) {
        setHasAgreedCatalog(true);
        setAgreedProducts(res.data.products || []);
        setAgreedCategories(res.data.categories || []);
        setAgreedTotal(res.data.total || 0);
        setActiveTab('agreed'); // Default to agreed tab when it exists
        // Init quantities for agreed products
        const newQ = {};
        (res.data.products || []).forEach(p => { if (!quantities[p._id]) newQ[p._id] = p.pricing?.minOrderQuantity || 1; });
        setQuantities(prev => ({ ...prev, ...newQ }));
      }
    } catch (err) {
      console.log('No custom catalog for this business');
    } finally {
      setAgreedLoading(false);
    }
  }, [id, isAuthenticated, user, quantities]);

  useEffect(() => {
    if (!id) return; // Wait for slug resolution
    if (activeTab === 'agreed') return; // Don't fetch full catalog when on agreed tab
    fetchProducts();
  }, [id, currentPage, selectedCategory, selectedSubcategory, searchTerm, sortBy, minPrice, maxPrice, activeTab]);

  // Filter products (sorting + price filtering is now server-side)
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    if (inStockOnly) {
      filtered = filtered.filter(product => product.availability?.inStock);
    }

    return filtered;
  }, [products, inStockOnly]);

  // Filter agreed products (client-side filtering on the custom catalog)
  const agreedFilteredProducts = useMemo(() => {
    let filtered = [...agreedProducts];
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    if (selectedSubcategory !== 'all') {
      filtered = filtered.filter(p => p.subcategory === selectedSubcategory);
    }
    if (searchInput) {
      const q = searchInput.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.brand?.toLowerCase().includes(q) ||
        p.sku?.toLowerCase().includes(q)
      );
    }
    if (inStockOnly) {
      filtered = filtered.filter(p => p.availability?.inStock);
    }

    return filtered;
  }, [agreedProducts, selectedCategory, searchInput, inStockOnly]);

  // Handlers
  const handleQuantityChange = (productId, value) => {
    const qty = parseInt(value);
    if (qty > 0) {
      setQuantities(prev => ({ ...prev, [productId]: qty }));
    }
  };

  const getMinQty = (product) => product?.pricing?.minOrderQuantity || 1;

  const handleAddToCart = (product) => {
    if (!isAuthenticated) {
      toast.error(t('productDetails.loginRequired'));
      return;
    }
    if (user?.role !== 'business') {
      toast.error(t('catalog.businessUserRequired'));
      return;
    }

    const quantity = quantities[product._id] || 1;
    const flashDeal = getFlashOfferForProduct(product._id);
    const productWithWholesaler = {
      ...product,
      wholesalerId: wholesaler._id,
      wholesaler: {
        _id: wholesaler._id,
        companyName: wholesaler.companyName,
        businessType: wholesaler.businessType
      },
      // Enterprise: carry hidePricing flag with cart item
      hidePricing: hidePricing || false,
      // ⚡ Flash offer data — travels with the cart item
      ...(flashDeal && {
        flashOfferPrice: flashDeal.offerPrice,
        flashOfferId: flashDeal.offerId,
        flashOfferTitle: flashDeal.offerTitle,
      })
    };
    
    addToCart(productWithWholesaler, quantity);
    // Removed alert for smoother experience
  };

  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory('all'); // Reset subcategory when category changes
    setCurrentPage(1);
    setProducts([]);
  };

  const handleSubcategoryChange = (subcategory) => {
    setSelectedSubcategory(subcategory);
    setCurrentPage(1);
    setProducts([]);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedSubcategory('all');
    setMinPrice('');
    setMaxPrice('');
    setInStockOnly(false);
    setSortBy('name_asc');
    setCurrentPage(1);
    if (id) sessionStorage.removeItem(`wd_filters_${id}`);
  };

  // Slug resolution loading
  if (slugResolving || (!isObjectId && !resolvedId && !error)) {
    return (
      <div className="min-h-screen bg-gray-50">
        {isStorefront ? <StorefrontNavbar wholesaler={{}} wholesalerId="" /> : <Navbar />}
        <div className="pt-16 lg:pt-0"><main className="max-w-7xl mx-auto px-4 py-12"><LoadingSpinner /></main></div>
      </div>
    );
  }

  // ✅ PHASE 6: Access Denied UI (SEPARATE SECTION - COMES FIRST)
  if (accessDenied && accessDeniedData?.wholesaler) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="container mx-auto px-4 py-8 pt-20">
          <div className="max-w-2xl mx-auto">
            {/* Back Button */}
            <Link to="/wholesalers" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.backToWholesalers', 'Back to Wholesalers')}
            </Link>

            {/* Access Denied Card */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              {/* Lock Icon */}
              <div className="w-20 h-20 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
                <Lock className="w-10 h-10 text-orange-600" />
              </div>
              
              {/* Wholesaler Logo */}
              {accessDeniedData.wholesaler.logo && (
                <div className="flex justify-center mb-4">
                  <img 
                    src={getImageUrl(accessDeniedData.wholesaler.logo)} 
                    alt={accessDeniedData.wholesaler.companyName}
                    className="h-16 object-contain"
                  />
                </div>
              )}
              
              {/* Wholesaler Name */}
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
                {accessDeniedData.wholesaler.companyName}
              </h2>
              
              {/* Description */}
              {accessDeniedData.wholesaler.description && (
                <p className="text-gray-600 text-center mb-6">
                  {accessDeniedData.wholesaler.description}
                </p>
              )}

              {/* Private Catalog Message */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
                <p className="text-orange-900 text-center font-medium">
                  🔒 {t('catalog.privateCatalogMessage', 'This wholesaler operates with a private catalog. Request partnership access to view their products and pricing.')}
                </p>
              </div>

              {/* Action Button */}
              {accessDeniedData.hasPendingRequest ? (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-6 py-4 rounded-lg text-center">
                  <p className="font-semibold">✓ {t('catalog.requestPending', 'Access Request Pending')}</p>
                  <p className="text-sm mt-2">
                    {t('catalog.requestPendingMessage', "We've notified {{wholesaler}}. You'll receive an email when they approve your request.", 
                      { wholesaler: accessDeniedData.wholesaler.companyName }
                    )}
                  </p>
                </div>
              ) : (
                <button
                  onClick={handleRequestAccess}
                  disabled={requestingAccess || !isAuthenticated}
                  className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
                >
                  {requestingAccess ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('catalog.sendingRequest', 'Sending Request...')}
                    </span>
                  ) : (
                    <>
                      <Unlock className="inline w-5 h-5 mr-2" />
                      {t('catalog.requestAccess', 'Request Partnership Access')}
                    </>
                  )}
                </button>
              )}

              {!isAuthenticated && (
                <p className="text-sm text-gray-500 text-center mt-4">
                  {t('catalog.loginRequired', 'Please log in to request access')}
                </p>
              )}

              {/* Why Private? */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-3">
                  {t('catalog.whyPrivate', 'Why is this catalog private?')}
                </h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <Check className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                    <span>{t('catalog.whyPrivateReason1', 'Protects pricing from competitors')}</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                    <span>{t('catalog.whyPrivateReason2', 'Ensures quality business relationships')}</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="w-4 h-4 mr-2 mt-0.5 text-green-600 flex-shrink-0" />
                    <span>{t('catalog.whyPrivateReason3', 'Provides personalized service to approved partners')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Loading State (SEPARATE SECTION - COMES SECOND)
  if (loading && currentPage === 1) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="lg" />
        </div>
        <Footer />
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-16 lg:pt-0">
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-800 mb-1">{t('common.error', 'Error')}</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
            <Link 
              to="/wholesalers" 
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mt-6 font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('catalog.backToWholesalers')}
            </Link>
          </main>
        </div>
      </div>
    );
  }

  if (!wholesaler) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {isStorefront ? (
        <StorefrontNavbar wholesaler={wholesaler} wholesalerId={id} slug={rawIdOrSlug} />
      ) : (
        <Navbar />
      )}
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb — hidden in storefront mode */}
          {!isStorefront && (
          <nav className="mb-6">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to="/" className="text-gray-500 hover:text-blue-600 transition-colors">
                  {t('common:common.home', 'Home')}
                </Link>
              </li>
              <li className="text-gray-300">/</li>
              <li>
                <Link to="/wholesalers" className="text-gray-500 hover:text-blue-600 transition-colors">
                  {t('catalog.wholesalers')}
                </Link>
              </li>
              <li className="text-gray-300">/</li>
              <li className="text-gray-900 font-medium truncate max-w-[200px]">
                {wholesaler.companyName}
              </li>
            </ol>
          </nav>
          )}

          {/* Wholesaler Header */}
          <WholesalerHeader wholesaler={wholesaler} id={id} isStorefront={isStorefront} />

          {/* ── Compact Discount Summary Bar ── */}
          {!hidePricing && (() => {
            const hasCatalog = catalogDiscounts && catalogDiscounts.count > 0;
            const discountList = Array.isArray(discounts) ? discounts : (discounts?.active ? [discounts] : []);
            const hasCustomer = discountList.some(d => d.percentage > 0);
            const hasProduct = isAuthenticated && user?.role === 'business' && Object.keys(productDiscounts).length > 0;
            if (!hasCatalog && !hasCustomer && !hasProduct) return null;

            return (
              <div className="bg-gradient-to-r from-indigo-100 via-purple-100 to-emerald-100 border border-indigo-300/60 rounded-xl mb-4 shadow-sm overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 px-4 py-3">
                  <Tag className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-700 mr-1">{t('catalog.yourDiscounts', 'Your discounts')}:</span>

                  {/* Catalog discount pills — blue */}
                  {hasCatalog && catalogDiscounts.discounts.map((cd, idx) => {
                    const pct = cd.type === 'flat' ? cd.percentage : cd.type === 'targeted' ? cd.targetPercentage : null;
                    if (!pct) return null;
                    const label = cd.type === 'flat'
                      ? t('catalog.offEverything', 'off everything')
                      : cd.type === 'targeted' ? `${cd.targetValue}` : t('catalog.tieredDiscount', 'tiered');
                    return (
                      <span key={`cat-${idx}`} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 border border-blue-300 text-blue-800 text-xs font-semibold rounded-full">
                        {Math.round(pct * 100) / 100}% {label}
                      </span>
                    );
                  })}

                  {/* Customer discount pills — emerald */}
                  {hasCustomer && discountList.filter(d => d.percentage > 0).map((d, idx) => (
                    <span key={`cust-${idx}`} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-100 border border-emerald-300 text-emerald-800 text-xs font-semibold rounded-full">
                      {d.percentage}% {t('catalog.customerDiscount', 'customer discount')}
                      {d.minimumOrderAmount > 0 && <span className="text-emerald-600 font-normal ml-0.5">(min €{d.minimumOrderAmount})</span>}
                    </span>
                  ))}

                  {/* Product discount pill — purple, clickable to expand */}
                  {hasProduct && (
                    <button
                      onClick={() => setDiscountDropdownOpen(!discountDropdownOpen)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 border border-purple-300 text-purple-800 text-xs font-semibold rounded-full hover:bg-purple-200 transition-colors"
                    >
                      {Object.keys(productDiscounts).length} {t('catalog.productDiscountPill', 'product discounts')}
                      <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${discountDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>

                {/* Product discounts dropdown */}
                {hasProduct && discountDropdownOpen && (
                  <div className="border-t border-gray-100 max-h-48 overflow-y-auto">
                    {Object.entries(productDiscounts).map(([productId, discount]) => {
                      const matchedProduct = products.find(p => p._id === productId) || agreedProducts.find(p => p._id === productId);
                      const displayName = matchedProduct?.name || discount.productName || productId;
                      return (
                        <div
                          key={productId}
                          className="flex items-center justify-between px-4 py-2 hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-50 last:border-0"
                          onClick={() => navigate(`/products/${productId}`)}
                        >
                          <span className="text-sm text-gray-700 truncate mr-3">{displayName}</span>
                          <span className="flex-shrink-0 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded">
                            -{discount.percentage}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ERP Pricing Info Banner */}
          {!hidePricing && wholesaler.hasErpPricing && isAuthenticated && user?.role === 'business' && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <span className="text-lg mt-0.5">💰</span>
                <div>
                  <p className="text-sm font-medium text-blue-900">{t('products:erp.pricingBanner', 'Your prices are calculated by the wholesaler\'s system. Add products to your cart to see your personalized pricing.')}</p>
                </div>
              </div>
            </div>
          )}

          {/* ═══════════════════════════════════════════════ */}
          {/* CATALOG TABS — Agreed / Full (Enterprise feature) */}
          {/* ═══════════════════════════════════════════════ */}
          {hasAgreedCatalog && (
            <div className="mb-6">
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => { setActiveTab('agreed'); setCurrentPage(1); setProducts([]); }}
                  className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                    activeTab === 'agreed'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  📋 {t('catalog.agreedCatalogue', 'Agreed Catalogue')}
                  <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{agreedTotal}</span>
                </button>
                <button
                  onClick={() => { setActiveTab('full'); setCurrentPage(1); }}
                  className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                    activeTab === 'full'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  📦 {t('catalog.fullCatalogue', 'Full Catalogue')}
                  <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{totalProducts}</span>
                </button>
              </div>
            </div>
          )}

          {/* Filter Bar */}
          <FilterBar
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            handleSearchSubmit={handleSearchSubmit}
            selectedCategory={selectedCategory}
            handleCategoryChange={handleCategoryChange}
            availableCategories={activeTab === 'agreed' ? agreedCategories : availableCategories}
            selectedSubcategory={selectedSubcategory}
            handleSubcategoryChange={handleSubcategoryChange}
            availableSubcategories={availableSubcategories}
            sortBy={sortBy}
            setSortBy={(v) => { setSortBy(v); setCurrentPage(1); setProducts([]); }}
            inStockOnly={inStockOnly}
            setInStockOnly={setInStockOnly}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            minPrice={minPrice}
            setMinPrice={(v) => { setMinPrice(v); setCurrentPage(1); setProducts([]); }}
            maxPrice={maxPrice}
            setMaxPrice={(v) => { setMaxPrice(v); setCurrentPage(1); setProducts([]); }}
            clearFilters={clearFilters}
            activeFiltersCount={activeFiltersCount}
            hidePricing={hidePricing}
            t={t}
            getCategoryLabel={getCategoryLabel}
          />

          {/* Results Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {activeTab === 'agreed' ? t('catalog.agreedCatalogue', 'Agreed Catalogue') : t('catalog.title')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('catalog.showingProducts', { 
                  count: activeTab === 'agreed' ? agreedFilteredProducts.length : filteredProducts.length, 
                  total: activeTab === 'agreed' ? agreedTotal : totalProducts 
                })}
              </p>
            </div>
            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                title={t('catalog.gridView', 'Grid view')}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                title={t('catalog.listView', 'List view')}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Products — Agreed Tab */}
          {activeTab === 'agreed' && hasAgreedCatalog && (
            <>
              {agreedLoading ? (
                <LoadingSpinner />
              ) : agreedFilteredProducts.length === 0 ? (
                <EmptyState clearFilters={clearFilters} t={t} />
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                  {agreedFilteredProducts.map((product) => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      discount={getProductDiscount(product)}
                      flashOffer={getFlashOfferForProduct(product._id)}
                      quantity={quantities[product._id] || getMinQty(product)}
                      onQuantityChange={handleQuantityChange}
                      onAddToCart={handleAddToCart}
                      onNavigate={navigate}
                      isAuthenticated={isAuthenticated}
                      userRole={user?.role}
                      hidePricing={hidePricing}
                      t={t}
                      getCategoryLabel={getCategoryLabel}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {agreedFilteredProducts.map((product) => (
                    <ProductListItem
                      key={product._id}
                      product={product}
                      discount={getProductDiscount(product)}
                      flashOffer={getFlashOfferForProduct(product._id)}
                      quantity={quantities[product._id] || getMinQty(product)}
                      onQuantityChange={handleQuantityChange}
                      onAddToCart={handleAddToCart}
                      onNavigate={navigate}
                      isAuthenticated={isAuthenticated}
                      userRole={user?.role}
                      hidePricing={hidePricing}
                      t={t}
                      getCategoryLabel={getCategoryLabel}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Products — Full Catalogue Tab */}
          {(activeTab === 'full' || !hasAgreedCatalog) && (
          <>
          {filteredProducts.length === 0 ? (
            <EmptyState clearFilters={clearFilters} t={t} />
          ) : (
            <>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={product._id}
                      product={product}
                      discount={getProductDiscount(product)}
                      flashOffer={getFlashOfferForProduct(product._id)}
                      quantity={quantities[product._id] || getMinQty(product)}
                      onQuantityChange={handleQuantityChange}
                      onAddToCart={handleAddToCart}
                      onNavigate={navigate}
                      isAuthenticated={isAuthenticated}
                      userRole={user?.role}
                      hidePricing={hidePricing}
                      t={t}
                      getCategoryLabel={getCategoryLabel}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredProducts.map((product) => (
                    <ProductListItem
                      key={product._id}
                      product={product}
                      discount={getProductDiscount(product)}
                      flashOffer={getFlashOfferForProduct(product._id)}
                      quantity={quantities[product._id] || getMinQty(product)}
                      onQuantityChange={handleQuantityChange}
                      onAddToCart={handleAddToCart}
                      onNavigate={navigate}
                      isAuthenticated={isAuthenticated}
                      userRole={user?.role}
                      hidePricing={hidePricing}
                      t={t}
                      getCategoryLabel={getCategoryLabel}
                    />
                  ))}
                </div>
              )}

              {/* Load More */}
              {currentPage < totalPages && !searchTerm && (
                <div className="mt-12 flex justify-center">
                  <button 
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="px-8 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('common.loading')}
                      </span>
                    ) : (
                      `${t('catalog.loadMore')} (${currentPage + 1}/${totalPages})`
                    )}
                  </button>
                </div>
              )}
            </>
          )}
          </>
          )}
        </main>
        {isStorefront ? (
          <footer className="border-t border-gray-100 mt-12 py-6">
            <div className="max-w-7xl mx-auto px-4 text-center">
              <p className="text-xs text-gray-400">
                Powered by <a href="/" className="text-blue-500 hover:text-blue-600 font-medium">WholesaleHub</a>
              </p>
            </div>
          </footer>
        ) : (
          <Footer />
        )}
      </div>
    </div>
  );
};

export default WholesalerDetail;